<template>
  <div>
    <Head title="Importador de Inventario por IMEI" />

    <!-- Header -->
    <div class="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
      <div class="flex items-center gap-4">
        <button 
          class="p-3 text-white bg-indigo-700 hover:bg-indigo-800 rounded-lg transition-colors"
          title="Volver atrás"
          @click="back()"
        >
          <i class="fa fa-arrow-left"></i>
        </button>
        <div>
          <div class="flex items-center gap-3">
            <h1 class="text-3xl font-bold text-gray-900">Importador de Inventario por IMEI</h1>
            <span class="px-2.5 py-0.5 text-xs font-semibold bg-purple-100 text-purple-800 rounded-full border border-purple-200">
              Ruta Especial / Oculta
            </span>
          </div>
          <p class="text-sm text-gray-500 mt-1">
            Alimenta Productos, Inventario por Tiendas, Proveedores y Traslados mediante archivos Excel estructurados por seriales/IMEIs.
          </p>
        </div>
      </div>
    </div>

    <!-- Quick Server File Runner Card -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
      <div class="lg:col-span-2 bg-gray-900 text-white rounded-2xl p-6 shadow-md relative overflow-hidden" style="background: linear-gradient(135deg, #0f172a 0%, #1e1b4b 100%);">
        <div class="absolute -right-6 -bottom-6 opacity-10 text-9xl">
          <i class="fa fa-barcode"></i>
        </div>

        <div class="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 relative z-10">
          <div>
            <div class="inline-flex items-center gap-2 px-3 py-1 bg-indigo-950/80 border border-indigo-700/50 rounded-full text-xs font-semibold text-indigo-200 mb-3">
              <i class="fa fa-file-excel text-emerald-400"></i>
              <span>Archivo Predeterminado Detectado</span>
            </div>
            <h2 class="text-2xl font-bold text-white">Procesar REOPRTE IMEI.xlsx</h2>
            <p class="text-gray-300 text-sm mt-2 max-w-xl">
              Ejecuta automáticamente la lectura de las pestañas <strong class="text-amber-300 font-bold">INGRESO</strong> (Carga de Equipos e Inventario) y <strong class="text-amber-300 font-bold">TRASLADOS</strong> (Movimientos entre tiendas) directamente desde el servidor.
            </p>
          </div>

          <button 
            @click="runDefaultFileImport"
            :disabled="processingDefault"
            class="px-6 py-3.5 bg-emerald-500 hover:bg-emerald-600 active:bg-emerald-700 text-white font-bold rounded-xl shadow-lg transition-all flex items-center gap-3 shrink-0 disabled:opacity-50 cursor-pointer border border-emerald-400/30"
          >
            <i v-if="processingDefault" class="fa fa-spinner fa-spin text-lg"></i>
            <i v-else class="fa fa-bolt text-lg"></i>
            <span>Procesar Archivo Servidor</span>
          </button>
        </div>
      </div>

      <!-- KPI Summary -->
      <div class="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm flex flex-col justify-between">
        <div>
          <div class="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Total IMEIs Registrados</div>
          <div class="text-4xl font-extrabold text-indigo-900">{{ totalImeis }}</div>
          <p class="text-xs text-gray-600 mt-2">
            Equipos móviles vinculados a productos y tiendas
          </p>
        </div>
        <div class="pt-4 border-t border-gray-100 mt-4 flex items-center gap-2 text-xs text-emerald-600 font-semibold">
          <i class="fa fa-check-circle"></i>
          <span>Sistema listo para recepción e inserción masiva</span>
        </div>
      </div>
    </div>

    <!-- Custom File Upload Form -->
    <div class="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden mb-8">
      <div class="p-6 border-b border-gray-100 bg-gray-50 flex items-center gap-3">
        <i class="fa fa-upload text-indigo-600 text-xl"></i>
        <div>
          <h2 class="text-lg font-bold text-gray-800">Cargar Archivo Excel Personalizado</h2>
          <p class="text-xs text-gray-500">Selecciona un archivo .xlsx con la estructura de IMEIs e Ingresos</p>
        </div>
      </div>

      <form @submit.prevent="uploadCustomFile" class="p-6 space-y-6">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label class="block text-sm font-semibold text-gray-700 mb-2">Depósito / Tienda Destino por Defecto</label>
            <select 
              v-model="uploadForm.deposit_id" 
              class="w-full form-select rounded-xl border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 text-sm"
            >
              <option value="">Seleccionar Tienda (Por defecto: Principal)...</option>
              <option v-for="dep in deposits" :key="dep.id" :value="dep.id">
                {{ dep.name }} ({{ dep.address || 'Sin dirección' }})
              </option>
            </select>
            <p class="text-xs text-gray-500 mt-1">Los ingresos sin tienda especificada se asignarán a esta ubicación.</p>
          </div>

          <div>
            <label class="block text-sm font-semibold text-gray-700 mb-2">Archivo Excel (.xlsx, .xls)</label>
            <input 
              type="file" 
              ref="fileInput"
              accept=".xlsx,.xls,.csv"
              @change="handleFileChange"
              class="w-full form-input rounded-xl border-gray-300 text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
            />
            <p v-if="uploadForm.errors.file" class="text-red-500 text-xs mt-1">{{ uploadForm.errors.file }}</p>
          </div>
        </div>

        <!-- Format Requirements Info Box -->
        <div class="bg-indigo-50 border border-indigo-200 rounded-xl p-4 text-xs text-indigo-900 space-y-2">
          <div class="font-bold flex items-center gap-2 text-indigo-900">
            <i class="fa fa-info-circle text-indigo-600"></i>
            <span>Estructura Aceptada del Excel:</span>
          </div>
          <ul class="list-disc list-inside space-y-1 text-gray-800">
            <li><strong>Pestañas de Ingreso (ej. INGRESO, INGRESO 2):</strong> Columnas `IMEI 1`, `DESCRIPCION`, `PROVEEDOR`, `FACTURA`, `FECHA`.</li>
            <li><strong>Pestañas de Traslados (ej. TRASLADOS):</strong> Columnas `IMEI 1`, `DESCRIPCION`, `ORIGEN`, `DESTINO`, `FECHA`.</li>
          </ul>
        </div>

        <div class="flex justify-end mt-6">
          <button 
            type="submit" 
            :disabled="uploadForm.processing || !uploadForm.file"
            class="px-6 py-3 text-white font-bold rounded-xl transition-all text-sm flex items-center gap-2 shadow-sm"
            :class="(uploadForm.processing || !uploadForm.file) ? 'bg-gray-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700 hover:shadow-md cursor-pointer'"
          >
            <i v-if="uploadForm.processing" class="fa fa-spinner fa-spin"></i>
            <i v-else class="fa fa-cloud-upload-alt"></i>
            <span v-if="!uploadForm.file">Selecciona un archivo primero</span>
            <span v-else>Procesar e Importar</span>
          </button>
        </div>
      </form>
    </div>

    <!-- Recent Imported IMEIs Table -->
    <div class="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
      <div class="p-6 border-b border-gray-100 flex justify-between items-center">
        <h2 class="text-lg font-bold text-gray-800">Últimos IMEIs Registrados en el Sistema</h2>
        <span class="text-xs text-gray-400">Mostrando últimos 15 registros</span>
      </div>

      <div class="overflow-x-auto">
        <table class="w-full whitespace-nowrap">
          <thead>
            <tr class="text-left bg-gray-50 text-gray-500 text-xs font-semibold uppercase tracking-wider">
              <th class="py-4 px-6">IMEI</th>
              <th class="py-4 px-6">Producto / Descripción</th>
              <th class="py-4 px-6">Tienda / Ubicación</th>
              <th class="py-4 px-6">Proveedor</th>
              <th class="py-4 px-6">Factura</th>
              <th class="py-4 px-6 text-center">Estado</th>
              <th class="py-4 px-6">Fecha Registro</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 text-sm">
            <tr v-for="item in recentImeis" :key="item.id" class="hover:bg-gray-50 transition-colors">
              <td class="py-4 px-6 font-mono font-bold text-indigo-900">
                {{ item.imei }}
              </td>
              <td class="py-4 px-6 font-medium text-gray-900">
                {{ item.product ? item.product.name : 'N/A' }}
              </td>
              <td class="py-4 px-6">
                <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold bg-gray-100 text-gray-700">
                  <i class="fa fa-store text-gray-400"></i>
                  <span>{{ item.deposit ? item.deposit.name : 'Sin Tienda' }}</span>
                </span>
              </td>
              <td class="py-4 px-6 text-gray-600">
                {{ item.provider ? item.provider.name : '-' }}
              </td>
              <td class="py-4 px-6 text-gray-600">
                {{ item.invoice_number || '-' }}
              </td>
              <td class="py-4 px-6 text-center">
                <span 
                  :class="[
                    'px-2.5 py-1 text-xs font-bold rounded-full uppercase',
                    item.status === 'available' ? 'bg-green-100 text-green-800' :
                    item.status === 'transferred' ? 'bg-blue-100 text-blue-800' :
                    item.status === 'sold' ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-800'
                  ]"
                >
                  {{ item.status === 'available' ? 'Disponible' : item.status === 'transferred' ? 'Trasladado' : item.status }}
                </span>
              </td>
              <td class="py-4 px-6 text-gray-500 text-xs">
                {{ formatDate(item.created_at) }}
              </td>
            </tr>

            <tr v-if="recentImeis.length === 0">
              <td colspan="7" class="text-center py-12 text-gray-500">
                No hay registros de IMEI guardados en el sistema.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

  </div>
</template>

<script>
import { Head } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'

export default {
  name: 'ImportWithImeis',

  components: {
    Head
  },

  layout: Layout,

  props: {
    deposits: Array,
    totalImeis: Number,
    recentImeis: Array,
    defaultFileExists: Boolean
  },

  data() {
    return {
      processingDefault: false,
      uploadForm: this.$inertia.form({
        deposit_id: '',
        file: null,
        use_default_file: false
      })
    }
  },

  methods: {
    back() {
      window.history.back()
    },
    formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    },
    handleFileChange(e) {
      this.uploadForm.file = e.target.files[0]
    },
    runDefaultFileImport() {
      if (!confirm('¿Deseas procesar el archivo predeterminado REOPRTE IMEI.xlsx del servidor?')) return
      
      this.processingDefault = true
      this.$inertia.post('/settings/import-with-imeis', {
        use_default_file: true
      }, {
        onFinish: () => {
          this.processingDefault = false
        },
        onSuccess: () => {
          this.$toast.success('Importación completada con éxito desde el servidor.')
        },
        onError: (err) => {
          const msg = err.error || 'Ocurrió un error en la importación.'
          this.$toast.error(msg)
        }
      })
    },
    uploadCustomFile() {
      if (!this.uploadForm.file) return
      
      this.uploadForm.post('/settings/import-with-imeis', {
        onSuccess: () => {
          this.uploadForm.reset('file')
          if (this.$refs.fileInput) {
            this.$refs.fileInput.value = ''
          }
          this.$toast.success('Archivo importado y procesado con éxito.')
        },
        onError: (err) => {
          const msg = err.error || 'Error al procesar el archivo.'
          this.$toast.error(msg)
        }
      })
    }
  }
}
</script>
